/**
 * web/src/hooks/mutations/useMarkConfirmed.ts
 * Optimistic mark-confirmed — drops the row from the tracked-emails list(s),
 * decrements badge instantly. Server resumes the paused workflow on success.
 *
 * Operates on the broad `qk.trackedEmails.root()` prefix so it reaches the
 * Confirmations page's actual list key (`qk.trackedEmails.list({eventId})`)
 * regardless of status filter — an earlier version targeted a
 * `['tracked-emails','pending']` key no mounted component read (F12).
 */
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { confirmEmail } from '../../api/tracked-emails';
import { toastApiError } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * Marks a tracked email confirmed. Optimistically removes it from every
 * cached tracked-emails list; rollback on error. The WS event arrives ms
 * later and the broad invalidate (handleBroadcast email_confirmed) reconciles.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST; cache writes across all tracked-emails list entries
 * @idempotent no — second call against an already-CONFIRMED email 409s
 */
export function useMarkConfirmed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => confirmEmail(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.trackedEmails.root() });
      // Snapshot every tracked-emails list variant so we can roll back.
      const previous = qc.getQueriesData<unknown[]>({ queryKey: qk.trackedEmails.root() });
      qc.setQueriesData<unknown[] | undefined>({ queryKey: qk.trackedEmails.root() }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.filter((e) => (e as { id?: string }).id !== id);
      });
      toast.success('Email confirmed — workflow resumed');
      return { previous };
    },
    onError: (err, _id, ctx) => {
      // Restore each snapshotted list entry.
      for (const [key, data] of ctx?.previous ?? []) {
        qc.setQueryData(key as QueryKey, data);
      }
      toastApiError(err, 'Confirm failed');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.trackedEmails.root() });
      qc.invalidateQueries({ queryKey: qk.stats.root() });
    },
  });
}
